'use client';
import { useContext, useState } from 'react';
import type { ResponseNativeCard } from '@/src/interfaces/data-contracts';
import { DeviceContext } from '@/src/store/contexts/device-context';
import { ClipboardChecklist, GoldMedal, HandphoneLock } from '../../ui/icons/icons';
import ImageWithFallback from '../../ui/image/image-with-fallback';
import Modal from '../../ui/modal/modal';
import { RelLink } from '../../ui/rel-link';
import { ProgrammableNC1 } from './programmable-nc-1';
import { ProgrammableNC2 } from './programmable-nc-2';
import { ProgrammableNC4 } from './programmable-nc-4';

type Props = {
  item: ResponseNativeCard;
};

export const ProgrammableNativeCard: React.FC<Props> = ({ item }) => {
  const { isMobile } = useContext(DeviceContext);
  const [showModal, setShowModal] = useState(false);
  const campaigns = item.nativeCardOfferCampaigns ?? [];

  const hasCampaigns = campaigns.length > 0;
  const firstCampaignType = hasCampaigns ? campaigns[0].type : undefined;
  const isCampaignTypeOne = hasCampaigns && (firstCampaignType as unknown as number) === 1;
  const isCampaignTypeNull = hasCampaigns && firstCampaignType === null;

  function handleCloseModal() {
    setShowModal(false);
  }

  return (
    <>
      <Modal
        className="md:max-w-auto h-auto max-w-[343px] px-4 pb-6 pt-5 md:min-w-[800px] md:px-8 md:pb-8 md:pt-[32px]"
        show={showModal}
        onClose={handleCloseModal}
        data-testid="nativeCardModal"
      >
        {
          <>
            <div className="mt-4 flex items-start justify-between">
              <div className="flex min-w-fit flex-1 items-center gap-5">
                <figure className="relative h-[52px] w-[104px]">
                  {
                    //@ts-ignore
                    <ImageWithFallback src={item.bankLogoPath} alt={item.name} title={item.name} fill />
                  }
                </figure>
                <div className="min-w-fit flex-1">
                  {
                    //@ts-ignore
                    <h2 className={'text-base text-black1000'}>{item.bankName || ''}</h2>
                  }
                  {
                    //@ts-ignore
                    <p className={'font-semibold text-black800'}>
                      {item.bankName ? `${item.bankName} Müşterisi Ol` : ''}
                    </p>
                  }
                  {/* <p className={'font-semibold text-black800'}>{product.qrPopUpTitle ?? ''}</p> */}
                </div>
              </div>
              {item.link && !isMobile && !item.isOfferPopUp && (
                <RelLink
                  href={item.link ?? ''}
                  target={item.target ?? '_blank'}
                  className={`flex ${isMobile ? 'flex-col justify-between' : 'justify-center'} ml-auto h-full w-fit items-center`}
                  data-testid="nativeCardLink"
                >
                  <div className="ml-auto w-fit rounded-[5px] bg-[#f26722] px-5 py-3 text-[16px]  font-semibold text-[#fff]">
                    Hemen Başvur <span className="ml-1">→</span>
                  </div>
                </RelLink>
              )}
            </div>
            <div>
              {item.link && isMobile && !item.isOfferPopUp && (
                <div className="mt-6 w-fit">
                  <RelLink href={item.link ?? ''} target={item.target ?? '_blank'} data-testid="nativeCardLink">
                    <div className="w-fit rounded-[5px] bg-[#f26722] px-5 py-3 text-[16px] font-semibold text-[#fff]">
                      Hemen Başvur <span className="ml-1">→</span>
                    </div>
                  </RelLink>
                </div>
              )}
            </div>
            {
              //@ts-ignore
              item.isOfferPopUp && item?.nativeCardPictures?.qrImagePath && !isMobile && (
                <div data-testid="qr-info" className="mb-4 mt-6 flex">
                  <div className="mr-[24px] flex h-[168px] flex-1 items-center rounded-[8px] border-[2px] border-[#d5dde3] p-4">
                    <figure className="relative mr-4 h-[128px] w-[128px] shrink-0" data-testid="qr-image">
                      {
                        <ImageWithFallback
                          //@ts-ignore
                          src={item.nativeCardPictures.qrImagePath}
                          alt={item.name}
                          title={item.name}
                          fill
                        />
                      }
                    </figure>
                    <span className="text-[16px] leading-[24px] text-[#5d5d5d]" data-testid="qr-text">
                      Mobil uygulamayı indirmek için QR kodu okut.
                    </span>
                  </div>

                  <div className="relative mr-[20px] w-fit">
                    <div
                      className='absolute left-[20px] top-[-7px] z-0 h-[181px] w-[2px]
                  bg-[#d5dde3] before:absolute before:bottom-0 before:left-[-5px] before:h-[12px] 
                  before:w-[12px] before:rounded-full before:bg-[#d5dde3]
                  before:content-[""] after:absolute after:right-[-5px] after:top-0 after:h-[12px] 
                  after:w-[12px] after:rounded-full after:bg-[#d5dde3] after:content-[""]
                  '
                    />
                    <ul className="z-1 my-4 flex flex-col gap-2">
                      <li className={'relative flex items-center gap-4'}>
                        <span className="rounded-full border-2 border-gray400 bg-white p-[6px]">
                          <HandphoneLock width={24} height={24} />
                        </span>
                        <p className={'text-[14px] leading-[24px] text-[#2b3a42]'}>
                          QR kodu okut, mobil uygulamayı indir.
                        </p>
                      </li>

                      <li className={'relative flex items-center gap-4'}>
                        <span className="rounded-full border-2 border-gray400 bg-white p-[6px]">
                          <ClipboardChecklist width={24} height={24} />
                        </span>
                        <p className={'text-[14px] leading-[24px] text-[#2b3a42]'}>Uzaktan banka müşterisi ol.</p>
                      </li>

                      <li className={'relative flex items-center gap-4'}>
                        <span className="rounded-full border-2 border-gray400 bg-white p-[6px]">
                          <GoldMedal width={24} height={24} />
                        </span>
                        <p className={'text-[14px] leading-[24px] text-[#2b3a42]'}>
                          Bankaya özel avantajlardan faydalan!
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
              )
            }
            {item.isConditionActive && (
              <div
                data-testid="conditions"
                className="accordion_content mt-6 max-h-[264px] w-full overflow-auto overscroll-contain rounded-[8px] bg-[#f6f6f6] p-3 md:mt-10"
                dangerouslySetInnerHTML={{ __html: item.conditions ?? '' }}
              />
            )}
          </>
        }
      </Modal>
      {/* <Modal
				show={showModal}
				onClose={handleCloseModal}
				customSize
				className="w-[calc(100%-32px)] rounded-xl px-4 py-5 md:w-[600px] md:px-10 md:py-10"
			>
				<div
					className="max-h-[calc(100vh-300px)] overflow-auto"
					// biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
					dangerouslySetInnerHTML={{ __html: item.conditions ?? "" }}
				/>
			</Modal> */}
      {isCampaignTypeOne && <ProgrammableNC4 item={item} setShowModal={setShowModal} />}
      {isCampaignTypeNull && <ProgrammableNC2 item={item} setShowModal={setShowModal} />}
      {!hasCampaigns && <ProgrammableNC1 item={item} setShowModal={setShowModal} />}
    </>
  );
};
